import { Button, Card, Input, message, Table, TableProps, Space } from 'antd';
import React, { useEffect } from 'react';
import {
  ApiFormsListTemplatesResponseDto,
  FormTemplateEntity,
} from '../../services/api/types/forms';
import { useSelector } from 'react-redux';
import { selectUserState } from '../../store/selectors/user';
import {
  apiFormsDeleteTemplate,
  apiFormsListTemplates,
  apiFormsUploadTemplate,
  apiFormsCreateTemplate,
} from '../../services/api/forms';
import Image from '../../components/Image';
import { Link } from 'react-router-dom';
import { RouterPaths } from '../../router/paths';
import { MinusOutlined, RobotOutlined } from '@ant-design/icons';
import CreateTemplateWithAIModal, {
  ApiFormsCreateTemplateRequestDto,
} from '../../components/CreateTemplateWithAIModal';
import TemplateScheduler from '../../components/TemplateScheduler';

interface TemplateRow extends FormTemplateEntity {
  key: string;
}

function TemplatesPage() {
  const [loading, setLoading] = React.useState(false);
  const [aiModalVisible, setAiModalVisible] = React.useState(false);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [pagination, setPagination] = React.useState<{
    limit: number;
    offset: number;
  }>({ limit: 10, offset: 0 });
  const { token } = useSelector(selectUserState);
  const [googleSheetUrl, setGoogleSheetUrl] = React.useState('');
  const [response, setResponse] =
    React.useState<ApiFormsListTemplatesResponseDto | null>(null);

  async function fetchData() {
    setLoading(true);
    try {
      if (token) {
        const [response] = await apiFormsListTemplates(token, { pagination });
        if (response?.templates) {
          setResponse(response);
        } else {
          message.error('Failed to fetch templates');
        }
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  }

  async function deleteTemplate(id: string) {
    if (token) {
      try {
        setLoading(true);
        const [res] = await apiFormsDeleteTemplate(token, { id });
        if (res.template) {
          message.success('Template deleted');
        } else {
          message.error('Failed to delete template');
        }

        await fetchData();
      } catch (e) {
        console.error(e);
        message.error('Failed to delete template');
      } finally {
        setLoading(false);
      }
    }
  }

  async function createTemplate() {
    if (token && googleSheetUrl?.length) {
      if (!googleSheetUrl.includes('https://')) {
        message.error('Invalid Google Sheet URL');
        return;
      }
      setLoading(true);
      try {
        const [res] = await apiFormsUploadTemplate(token, { googleSheetUrl });

        if (res?.template) {
          message.success('Template created');
          setGoogleSheetUrl('');
          await fetchData();
        } else {
          message.error('Failed to create template');
        }
      } catch (error) {
        console.error(error);
        message.error('Failed to create template');
      } finally {
        setLoading(false);
      }
    }
  }

  async function createTemplateWithAI(
    values: ApiFormsCreateTemplateRequestDto,
  ) {
    if (!token) return;

    setAiLoading(true);
    try {
      const [res, status] = await apiFormsCreateTemplate(token, values);

      if (status === 200 || status === 201) {
        if (res?.template) {
          message.success('🎉 AI Template created successfully!');
          await fetchData();
        } else {
          message.error('Failed to create template');
        }
      } else {
        message.error('Failed to create template');
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to create template with AI');
    } finally {
      setAiLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
  }, [token, pagination]);

  const columns: TableProps<TemplateRow>['columns'] = [
    {
      title: 'Order',
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, record) => (
        <Link
          to={RouterPaths.SINGLE_TEMPLATE.replace(
            ':templateId',
            record.aliasId,
          )}
        >
          {v}
        </Link>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Color',
      dataIndex: 'color',
      key: 'color',
      render: (v) => (
        <div
          style={{
            backgroundColor: v,
            width: 25,
            height: 25,
            borderRadius: '50%',
          }}
        />
      ),
    },
    {
      title: 'Image',
      dataIndex: 'image',
      key: 'image',
      render: (v, record) =>
        record.image?.id ? <Image id={record.image?.id} /> : 'No image',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (v, record) => (
        <Button
          type="primary"
          danger
          icon={<MinusOutlined />}
          onClick={async () => {
            await deleteTemplate(record.aliasId);
          }}
        />
      ),
    },
  ];

  const dataSource =
    response?.templates?.map((template) => ({
      key: template.id.toString(),
      ...template,
    })) || ([] as TemplateRow[]);

  const isLoading = !response?.templates || loading || aiLoading;

  return (
    <>
      {/* Google Sheets Template Scheduler */}
      {token && (
        <div style={{ marginBottom: 16 }}>
          <TemplateScheduler token={token} onTemplateProcessed={fetchData} />
        </div>
      )}

      <Card title="Templates">
        <div style={{ marginBottom: 24 }}>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* AI Template Creation */}
            <div>
              <Button
                type="primary"
                icon={<RobotOutlined />}
                onClick={() => {
                  setAiModalVisible(true);
                }}
                disabled={isLoading || aiLoading}
                size="large"
                style={{
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  boxShadow: '0 4px 15px 0 rgba(102, 126, 234, 0.3)',
                }}
              >
                Create Single Template with AI
              </Button>
              <span style={{ marginLeft: 12, color: '#666', fontSize: '14px' }}>
                Create one template instantly, or use the Google Sheets
                scheduler above for batch processing
              </span>
            </div>

            {/* Traditional Google Sheets Creation */}
            <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
              <Input
                placeholder="Google Sheet URL (alternative method)"
                value={googleSheetUrl}
                onChange={(e) => {
                  setGoogleSheetUrl(e.target.value);
                }}
                disabled={isLoading || aiLoading}
                style={{ flex: 1 }}
              />
              <Button
                disabled={isLoading || aiLoading || !googleSheetUrl?.length}
                type="default"
                htmlType="button"
                onClick={createTemplate}
              >
                Create from Sheet
              </Button>
            </div>
          </Space>
        </div>
        <Table
          dataSource={dataSource}
          columns={columns}
          pagination={{
            total: response?.pagination.total || 0,
            pageSize: pagination.limit,
          }}
          loading={isLoading}
          onChange={(pagination) => {
            setPagination({
              limit: pagination.pageSize || 10,
              offset:
                (pagination.current || 1) * (pagination.pageSize || 10) -
                (pagination.pageSize || 10),
            });
          }}
        />

        <CreateTemplateWithAIModal
          visible={aiModalVisible}
          onCancel={() => {
            setAiModalVisible(false);
          }}
          onSuccess={() => {
            setAiModalVisible(false);
          }}
          loading={aiLoading}
          onSubmit={createTemplateWithAI}
        />
      </Card>
    </>
  );
}

export default TemplatesPage;
